import { Component, computed, EventEmitter, HostBinding, input, Input, Output, ViewChild } from '@angular/core';
import { ActiveLookup, Association, AssociationKeys, Lookups, Messages } from '@ag-common-lib/public-api';
import { DxListComponent } from 'devextreme-angular';
import { ItemClickEvent } from 'devextreme/ui/list';
import { SelectAssociationService } from './select-association.service';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { isEqual } from 'lodash';

@Component({
  selector: 'ag-shr-select-association',
  templateUrl: './select-association.component.html',
  styleUrls: ['./select-association.component.scss'],
  providers: [SelectAssociationService],
})
export class SelectAssociationComponent {
  @HostBinding('class') protected className = 'select-association';
  @ViewChild('listRef') listComponent: DxListComponent;

  selectedAssociationSig = input.required({ alias: 'association' });
  associationsSig = input.required<Association[]>({ alias: 'associations' });

  @Input() associationKeyExpr: string | ((association: Partial<Association>) => string);
  @Input() isReadonly: boolean = false;
  @Input() isListVisible: boolean = true;
  @Input() selectorHidden: boolean = false;
  @Input() newAssociationTitle: string = 'Add new Emergency Contact';
  @Input() validationGroup: string;
  @Input() propertiesToAssign: Set<string>;
  @Input() useOnAssociationChangedOnly = false;
  @Input() onAssociationChanged: (association: Partial<Association>, associationDbId: string) => void;
  @Output() handleRelationshipTypeSelectionChanged = new EventEmitter<ActiveLookup>();

  protected associationsDataSourceSig = computed<any[]>(
    () => {
      const associations = this.associationsSig();
      const associationTypeMap = this.selectAssociationService.associationTypeMapSig();

      const list = [];

      associations?.forEach(association => {
        const key = this.selectAssociationService.pickAssociationKey(this.associationKeyExpr, association);
        const firstName = association?.[AssociationKeys.firstName];
        const lastName = association?.[AssociationKeys.lastName];
        const associationType = association?.[AssociationKeys.associationType];
        const associationTypeDescription = associationTypeMap.get(associationType);
        let description = [firstName, lastName].filter(Boolean).join(' ');

        if (associationTypeDescription) {
          description += ` (${associationTypeDescription})`;
        }

        list.push({ key, description, association: association });
      });

      list.push({ key: null, description: 'Add another', association: {} });

      return list;
    },
    { equal: isEqual },
  );

  protected selectedAssociationKeysSig = computed(
    () => {
      const selectedAssociation = this.selectedAssociationSig();
      const key =
        this.selectAssociationService.pickAssociationKey(this.associationKeyExpr, selectedAssociation) ?? null;
      const associationsDataSource = this.associationsDataSourceSig();
      const selectedAssociationKeys = associationsDataSource?.find(item => item?.key === key);

      if (!selectedAssociationKeys) {
        return [null];
      }

      return [key];
    },
    { equal: isEqual },
  );

  protected keyExpr = this.selectAssociationService.pickAssociationKey;
  protected selectedAssociationKeys: string[];

  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly Lookups = Lookups;
  readonly Messages = Messages;

  readonly AssociationKeys = AssociationKeys;

  constructor(private selectAssociationService: SelectAssociationService) {}

  protected handleSelectAssociation = ($event: ItemClickEvent) => {
    const itemData = $event?.itemData;
    const association = itemData?.association;
    const associationDbId = association?.[BaseModelKeys.dbId];
    const formData = this.selectedAssociationSig();
    if (this.useOnAssociationChangedOnly) {
      return this.onAssociationChanged(association, association?.[BaseModelKeys.dbId]);
    }
    this.selectAssociationService.updateAssociation(
      formData,
      association,
      this.propertiesToAssign,
      this.onAssociationChanged,
    );
  };
}
